import { Injectable, Injector } from '@angular/core';
import { DwMenu, DwMenuCategory, DwMenuHelperService, DwMenuProcessor } from '@devwareapps/devware-cap';

interface SidebarSection {
  /** Section header text. Omitted for ungrouped blocks (Dashboard, Resources). */
  title?: string;
  /** Menu displays, lower-cased, in the order they should render inside the section. */
  displays: string[];
  /** Render a `nav-divider` above this section. */
  dividerBefore?: boolean;
  /** Side menus this config doesn't know about land here, keeping their original order. */
  catchAll?: boolean;
}

/**
 * Sidebar information architecture. The menu items themselves live in Devware
 * route metadata (database); this only decides grouping and order, so items that
 * are permission-trimmed for the current user simply drop out of their section.
 */
const SIDEBAR_SECTIONS: SidebarSection[] = [
  { displays: ['dashboard'] },
  { title: 'Learning', displays: ['my courses', 'my assignments', 'achievements'] },
  { title: 'Instruction', displays: ['my students', 'assignments'] },
  { title: 'Administration', displays: ['manage users', 'reporting', 'lms admin'] },
  { displays: [], catchAll: true },
  { dividerBefore: true, displays: ['resources'] }
];

@Injectable()
export class SidebarSectionMenuProcessorService implements DwMenuProcessor {
  // Injector rather than the service directly: DwMenuHelperService injects the
  // processor token, so a constructor dependency would be a DI cycle.
  constructor(private readonly injector: Injector) {}

  processAllMenus(menus: DwMenu[]): DwMenu[] {
    if (!menus?.length) {
      return menus;
    }

    // Re-runs on every menu refresh, so drop anything a previous pass injected.
    const sourceMenus = menus.filter(menu => !this.isSynthetic(menu));

    const sideMenus = sourceMenus.filter(menu => this.isVisibleSideMenu(menu));
    if (!sideMenus.length) {
      return sourceMenus;
    }

    const otherMenus = sourceMenus.filter(menu => !this.isVisibleSideMenu(menu));

    const claimed = new Set<DwMenu>();
    const ordered: DwMenu[] = [];

    for (const section of SIDEBAR_SECTIONS) {
      const sectionMenus = section.catchAll
        ? sideMenus.filter(menu => !claimed.has(menu) && !this.isConfigured(menu))
        : this.findSectionMenus(section, sideMenus, claimed);

      if (!sectionMenus.length) {
        continue;
      }

      if (section.dividerBefore && ordered.length) {
        ordered.push(this.buildSyntheticMenu('sidebar-divider', { isDivider: true }));
      }
      if (section.title) {
        ordered.push(this.buildSyntheticMenu(`sidebar-title-${section.title}`, { isTitle: true, name: section.title.toUpperCase() }));
      }

      for (const menu of sectionMenus) {
        ordered.push(menu);
        claimed.add(menu);
      }
    }

    // Any side menu the loop above skipped (no catch-all section configured)
    // still has to render, otherwise it disappears from the sidebar entirely.
    const unclaimed = sideMenus.filter(menu => !claimed.has(menu));

    return [...ordered, ...unclaimed, ...otherMenus];
  }

  private findSectionMenus(section: SidebarSection, sideMenus: DwMenu[], claimed: Set<DwMenu>): DwMenu[] {
    const sectionMenus: DwMenu[] = [];

    for (const display of section.displays) {
      const menu = sideMenus.find(sideMenu => !claimed.has(sideMenu) && this.getDisplay(sideMenu) === display);
      if (menu) {
        sectionMenus.push(menu);
      }
    }

    return sectionMenus;
  }

  private isConfigured(menu: DwMenu): boolean {
    const display = this.getDisplay(menu);

    return SIDEBAR_SECTIONS.some(section => section.displays.indexOf(display) >= 0);
  }

  private getDisplay(menu: DwMenu): string {
    return (menu.display || menu.data?.display || '').trim().toLowerCase();
  }

  /**
   * Mirrors the filtering Devware applies after processors run, so a section
   * header is never emitted above items the current user can't see.
   */
  private isVisibleSideMenu(menu: DwMenu): boolean {
    const data = menu.data;
    if (!data) {
      return false;
    }
    if ((data.category || DwMenuCategory.sideMenu) !== DwMenuCategory.sideMenu) {
      return false;
    }
    if (data.showInMenu === false || data.excludeFromMainMenu || data.isDivider) {
      return false;
    }

    const menuHelperService = this.injector.get(DwMenuHelperService);

    return menuHelperService.canAccessMenu(menu) && menuHelperService.canViewMenuFromProcessors(menu);
  }

  private isSynthetic(menu: DwMenu): boolean {
    return (menu as any).isTitle === true || (menu as any).isDivider === true;
  }

  private buildSyntheticMenu(menuKey: string, extra: { isTitle?: boolean; isDivider?: boolean; name?: string }): DwMenu {
    // `dw-app-sidebar-nav` renders `isTitle`/`isDivider` entries as chrome, but the
    // menu state is still walked by the builder services, so `data`/`children` must exist.
    // The sentinel url keeps these from matching a real route in getCurrentMenu().
    return {
      menuKey,
      display: extra.name || '',
      url: `#${menuKey}`,
      path: `#${menuKey}`,
      children: [],
      data: {},
      ...extra
    } as DwMenu;
  }
}
